(() => {
  'use strict';

  /*
   * ============================================================
   * GOAL JAPAN チーム専用導線管理
   * ============================================================
   *
   * 役割：
   *
   * 1. TEAM GOALSTUDIOへのリンクに付与された情報を保存
   * 2. チーム専用ページ経由者だけに専用要素を表示
   * 3. チームログイン中はヘッダーを専用表示へ切り替え
   * 4. ロゴのトップページリンクを無効化
   * 5. 「買い物を続ける」をチーム専用ページへ変更
   * 6. カートドロワーなどのAjax再描画にも対応
   */

  /*
   * TEAM GOALSTUDIOカテゴリを選択したことを示すキー
   */
  const ACCESS_KEY = 'goal_team_access';

  /*
   * チーム識別コード
   *
   * 例：
   * mamedofc
   */
  const TEAM_CODE_KEY = 'goal_team_code';

  /*
   * 既存のチームログイン機能で保存されるキー
   *
   * team_access_handle：
   * ログインしたチームの専用コレクションハンドル
   *
   * team_access_until：
   * チームログインの有効期限
   */
  const LOGIN_HANDLE_KEY = 'team_access_handle';
  const LOGIN_UNTIL_KEY = 'team_access_until';

  /*
   * MutationObserverの多重処理を防ぐための変数
   */
  let updateScheduled = false;

  /**
   * URLに付与されたチーム情報を取得し、
   * 現在のブラウザタブ内へ一時保存します。
   *
   * URL例：
   *
   * /collections/team-goalstudio
   * ?team_access=1
   * &team=mamedofc
   */
  function saveTeamAccessFromUrl() {
    const currentUrl = new URL(window.location.href);
    const params = currentUrl.searchParams;

    if (params.get('team_access') !== '1') {
      return;
    }

    const teamCode = (params.get('team') || '').trim();

    if (!teamCode) {
      return;
    }

    try {
      sessionStorage.setItem(ACCESS_KEY, '1');
      sessionStorage.setItem(TEAM_CODE_KEY, teamCode);
    } catch (error) {
      console.error(
        '[GOAL team access] チーム経由情報の保存に失敗しました。',
        error
      );

      return;
    }

    /*
     * 保存後、URLから識別用パラメータを削除します。
     */
    params.delete('team_access');
    params.delete('team');

    const cleanUrl =
      currentUrl.pathname +
      (params.toString() ? `?${params.toString()}` : '') +
      currentUrl.hash;

    window.history.replaceState(
      {},
      document.title,
      cleanUrl
    );
  }

  /**
   * TEAM GOALSTUDIOカテゴリを経由しているか返します。
   */
  function hasTeamAccess() {
    try {
      return sessionStorage.getItem(ACCESS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  /**
   * 保存されているチーム識別コードを返します。
   */
  function getTeamCode() {
    try {
      return sessionStorage.getItem(TEAM_CODE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * チームログイン時に保存されている
   * 専用コレクションハンドルを返します。
   */
  function getLoginHandle() {
    try {
      return sessionStorage.getItem(LOGIN_HANDLE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * チームログインの有効期限を返します。
   */
  function getLoginUntil() {
    try {
      return Number(
        sessionStorage.getItem(LOGIN_UNTIL_KEY) || 0
      );
    } catch (error) {
      return 0;
    }
  }

  /**
   * 現在のチームログインセッションが有効か確認します。
   */
  function hasValidTeamLoginSession() {
    const allowedHandle = getLoginHandle();
    const accessUntil = getLoginUntil();

    return (
      allowedHandle !== '' &&
      accessUntil > 0 &&
      Date.now() <= accessUntil
    );
  }

  /**
   * チーム専用コレクションのURLを返します。
   *
   * 例：
   * /collections/mamedofc
   */
  function getTeamCollectionUrl() {
    const handle = getLoginHandle();

    if (!handle) {
      return '';
    }

    return `/collections/${encodeURIComponent(handle)}`;
  }

  /**
   * data-team-access-only が付いた要素を、
   * TEAM GOALSTUDIOカテゴリ経由の場合だけ表示します。
   */
  function updateTeamOnlyElements(root = document) {
    const active = hasTeamAccess();

    root
      .querySelectorAll('[data-team-access-only]')
      .forEach((element) => {
        element.hidden = !active;
        element.setAttribute(
          'aria-hidden',
          active ? 'false' : 'true'
        );
      });
  }

  /**
   * ロゴリンクを無効化します。
   */
  function disableLogoLinks() {
    document
      .querySelectorAll('.header__heading-link')
      .forEach((logoLink) => {
        const currentHref = logoLink.getAttribute('href');

        if (
          currentHref &&
          !logoLink.dataset.goalOriginalHref
        ) {
          logoLink.dataset.goalOriginalHref = currentHref;
        }

        logoLink.removeAttribute('href');
        logoLink.setAttribute('aria-disabled', 'true');
        logoLink.setAttribute('tabindex', '-1');
      });
  }

  /**
   * 通常サイト表示に戻った場合、
   * ロゴリンクを元に戻します。
   */
  function restoreLogoLinks() {
    document
      .querySelectorAll('.header__heading-link')
      .forEach((logoLink) => {
        const originalHref =
          logoLink.dataset.goalOriginalHref;

        if (originalHref) {
          logoLink.setAttribute(
            'href',
            originalHref
          );
        }

        logoLink.removeAttribute('aria-disabled');
        logoLink.removeAttribute('tabindex');
      });
  }

  /**
   * リンクの表示文字から、
   * 「買い物を続ける」に該当するか確認します。
   */
  function isContinueShoppingText(link) {
    const text = (link.textContent || '')
      .replace(/\s+/g, '')
      .trim();

    return (
      text.includes('買い物を続ける') ||
      text.includes('ショッピングを続ける') ||
      text.toLowerCase().includes('continue shopping')
    );
  }

  /**
   * Shopifyの商品一覧リンクか確認します。
   */
  function isAllProductsLink(link) {
    const rawHref = link.getAttribute('href');

    if (!rawHref) {
      return false;
    }

    try {
      const url = new URL(rawHref, window.location.origin);
      const pathname = url.pathname.replace(/\/+$/, '');

      return (
        pathname === '/collections/all' ||
        pathname === '/products'
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * カート内の「買い物を続ける」を、
   * ログイン中のチーム専用ページへ変更します。
   *
   * カートページとカートドロワーの両方に対応します。
   */
  function updateContinueShoppingLinks(root = document) {
    const isTeamLoginActive =
      hasValidTeamLoginSession();

    const teamCollectionUrl =
      getTeamCollectionUrl();

    root
      .querySelectorAll('a[href]')
      .forEach((link) => {
        const isTarget =
          isContinueShoppingText(link) ||
          (
            isAllProductsLink(link) &&
            (
              link.closest('.cart-drawer') ||
              link.closest('.cart__warnings') ||
              link.closest('.cart-items') ||
              link.closest('cart-drawer')
            )
          );

        if (!isTarget) {
          return;
        }

        /*
         * 元のリンク先を一度だけ保存します。
         */
        if (!link.dataset.goalOriginalHref) {
          link.dataset.goalOriginalHref =
            link.getAttribute('href') || '';
        }

        /*
         * チームログイン中は専用ページへ変更します。
         */
        if (
          isTeamLoginActive &&
          teamCollectionUrl
        ) {
          link.setAttribute(
            'href',
            teamCollectionUrl
          );

          link.dataset.goalTeamContinueShopping = 'true';

          return;
        }

        /*
         * 通常利用時は元のリンク先へ戻します。
         */
        const originalHref =
          link.dataset.goalOriginalHref;

        if (originalHref) {
          link.setAttribute(
            'href',
            originalHref
          );
        }

        delete link.dataset.goalTeamContinueShopping;
      });
  }

  /**
   * チームログイン中だけ、
   * HTML要素へ専用モード用クラスを追加します。
   */
  function updateTeamHeaderMode() {
    const active = hasValidTeamLoginSession();

    document.documentElement.classList.toggle(
      'goal-team-mode',
      active
    );

    if (active) {
      disableLogoLinks();
    } else {
      restoreLogoLinks();
    }
  }

  /**
   * チームログインの有効期限が切れた場合に、
   * 専用表示を解除します。
   */
  function checkTeamSessionExpiry() {
    if (!hasValidTeamLoginSession()) {
      document.documentElement.classList.remove(
        'goal-team-mode'
      );

      restoreLogoLinks();
      updateContinueShoppingLinks();
    }
  }

  /**
   * DOM更新処理を1フレームにまとめます。
   */
  function scheduleUpdate() {
    if (updateScheduled) {
      return;
    }

    updateScheduled = true;

    window.requestAnimationFrame(() => {
      updateScheduled = false;

      updateTeamOnlyElements();
      updateTeamHeaderMode();
      updateContinueShoppingLinks();
    });
  }

  /**
   * カートドロワーなどがAjaxで再描画された場合にも、
   * 表示条件を再適用します。
   */
  function observeDynamicElements() {
    if (!document.body) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      const hasAddedNodes = mutations.some(
        (mutation) => mutation.addedNodes.length > 0
      );

      if (!hasAddedNodes) {
        return;
      }

      scheduleUpdate();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * チーム専用導線の保存情報のみを削除します。
   */
  function clearTeamAccessData() {
    try {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(TEAM_CODE_KEY);
    } catch (error) {
      console.error(
        '[GOAL team access] チーム経由情報の削除に失敗しました。',
        error
      );
    }

    updateTeamOnlyElements();
    updateTeamHeaderMode();
    updateContinueShoppingLinks();
  }

  /**
   * チームログイン情報を含めてすべて削除します。
   */
  function clearAllTeamData() {
    try {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(TEAM_CODE_KEY);
      sessionStorage.removeItem(LOGIN_HANDLE_KEY);
      sessionStorage.removeItem(LOGIN_UNTIL_KEY);
    } catch (error) {
      console.error(
        '[GOAL team access] チーム情報の削除に失敗しました。',
        error
      );
    }

    updateTeamOnlyElements();
    updateTeamHeaderMode();
    updateContinueShoppingLinks();
  }

  /**
   * 初期化処理
   */
  function initialize() {
    /*
     * URLのチーム経由情報を取得
     */
    saveTeamAccessFromUrl();

    /*
     * 条件付き要素を更新
     */
    updateTeamOnlyElements();

    /*
     * ヘッダーを専用表示へ変更
     */
    updateTeamHeaderMode();

    /*
     * 「買い物を続ける」のリンク先を更新
     */
    updateContinueShoppingLinks();

    /*
     * AjaxによるDOM再描画を監視
     */
    observeDynamicElements();

    /*
     * 30秒ごとにログイン期限を確認
     */
    window.setInterval(
      checkTeamSessionExpiry,
      30000
    );

    /*
     * 他のスクリプトやブラウザコンソールから
     * 利用できる共通機能を公開します。
     */
    window.GoalTeamAccess = {
      /*
       * TEAM GOALSTUDIOカテゴリ経由か
       */
      isActive: hasTeamAccess,

      /*
       * チームログインが有効か
       */
      isLoginSessionActive:
        hasValidTeamLoginSession,

      /*
       * チーム識別コードを取得
       */
      getTeamCode,

      /*
       * ログイン中の専用コレクションハンドルを取得
       */
      getLoginHandle,

      /*
       * チーム専用コレクションURLを取得
       */
      getTeamCollectionUrl,

      /*
       * 表示状態を再判定
       */
      refresh() {
        updateTeamOnlyElements();
        updateTeamHeaderMode();
        updateContinueShoppingLinks();
      },

      /*
       * TEAM GOALSTUDIO経由情報のみ削除
       */
      clear: clearTeamAccessData,

      /*
       * チームログイン情報を含めて全削除
       */
      clearAll: clearAllTeamData
    };
  }

  /*
   * DOMの読み込み完了後に初期化します。
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();